import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { multiFactor } from 'firebase/auth';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { ShieldCheck, ShieldAlert, Loader2 } from 'lucide-react';

export const MFAEnrollment: React.FC = () => {
    const { enrollMFA, verifyMFA, unenrollMFA, user } = useAuth();
    const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
    const [verificationCode, setVerificationCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    // Check if user already has MFA enrolled
    const isEnrolled = user && multiFactor(user).enrolledFactors.length > 0;

    const handleStartEnrollment = async () => {
        setLoading(true);
        setError(null);
        try {
            const url = await enrollMFA();
            setQrCodeUrl(url);
        } catch (err: any) {
            setError(err.message || 'Failed to start enrollment');
        } finally {
            setLoading(false);
        }
    };

    const handleVerify = async () => {
        setLoading(true);
        setError(null);
        try {
            await verifyMFA(verificationCode);
            setSuccess(true);
            setQrCodeUrl(null);
            setVerificationCode('');
        } catch (err: any) {
            setError(err.message || 'Invalid verification code');
        } finally {
            setLoading(false);
        }
    };

    const handleUnenroll = async () => {
        if (!confirm('Are you sure you want to disable MFA? This will lower your account security.')) return;

        setLoading(true);
        setError(null);
        try {
            await unenrollMFA();
            setSuccess(false); // Reset success state
        } catch (err: any) {
            setError(err.message || 'Failed to disable MFA');
        } finally {
            setLoading(false);
        }
    };

    if (isEnrolled && !qrCodeUrl) {
        return (
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <ShieldCheck className="h-5 w-5 text-green-500" />
                        Multi-Factor Authentication
                    </CardTitle>
                    <CardDescription>
                        Your account is secured with MFA.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <Alert className="bg-green-50 border-green-200">
                        <ShieldCheck className="h-4 w-4 text-green-600" />
                        <AlertTitle className="text-green-800">MFA Enabled</AlertTitle>
                        <AlertDescription className="text-green-700">
                            Two-factor authentication is currently active on your account.
                        </AlertDescription>
                    </Alert>
                </CardContent>
                <CardFooter>
                    <Button variant="destructive" onClick={handleUnenroll} disabled={loading}>
                        {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                        Disable MFA
                    </Button>
                </CardFooter>
            </Card>
        );
    }

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <ShieldAlert className="h-5 w-5 text-amber-500" />
                    Multi-Factor Authentication
                </CardTitle>
                <CardDescription>
                    Add an extra layer of security to your account using an authenticator app.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {error && (
                    <Alert variant="destructive">
                        <AlertTitle>Error</AlertTitle>
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}

                {success && (
                    <Alert className="bg-green-50 border-green-200">
                        <ShieldCheck className="h-4 w-4 text-green-600" />
                        <AlertTitle className="text-green-800">Success</AlertTitle>
                        <AlertDescription className="text-green-700">
                            MFA has been successfully enabled!
                        </AlertDescription>
                    </Alert>
                )}

                {!qrCodeUrl ? (
                    <div className="text-sm text-gray-500">
                        Protect your account by requiring a code from an authenticator app (like Google Authenticator) in addition to your password.
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="flex flex-col items-center justify-center p-4 border rounded-lg bg-white">
                            <QRCodeSVG value={qrCodeUrl} size={192} />
                            <p className="mt-2 text-xs text-gray-500 text-center">
                                Scan this QR code with your authenticator app
                            </p>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium">Verification Code</label>
                            <Input
                                placeholder="Enter 6-digit code"
                                value={verificationCode}
                                onChange={(e) => setVerificationCode(e.target.value)}
                                maxLength={6}
                            />
                        </div>
                    </div>
                )}
            </CardContent>
            <CardFooter>
                {!qrCodeUrl ? (
                    <Button onClick={handleStartEnrollment} disabled={loading}>
                        {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                        Enable MFA
                    </Button>
                ) : (
                    <div className="flex gap-2 w-full">
                        <Button variant="outline" onClick={() => setQrCodeUrl(null)} disabled={loading} className="flex-1">
                            Cancel
                        </Button>
                        <Button onClick={handleVerify} disabled={loading || verificationCode.length < 6} className="flex-1">
                            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                            Verify & Enable
                        </Button>
                    </div>
                )}
            </CardFooter>
        </Card>
    );
};
